import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export interface FilterObject {
    [key: string]: string | string[];
}

export const countNonEmptyProperties = (obj: Record<string, any>): number => {
    let count = 0;
    for (const key in obj) {
        if (obj.hasOwnProperty(key)) {
            const value = obj[key];
            if (value !== null && value !== undefined && value !== '' && value.length !== 0) {
                count++;
            }
        }
    }
    return count;
};

export const endsWithList = (str: string): boolean => {
    return str.endsWith('_list');
};

export const parseFilterString = (filterString: string): FilterObject => {
    const filterObject: FilterObject = {};
    if (!filterString) return filterObject;
    const keyValuePairs = filterString.split('&');
    if (keyValuePairs?.length < 1) return filterObject;
    keyValuePairs.forEach((pair) => {
        const [key, value] = pair.split('=');
        if (key && value) {
            const decodedValue = decodeURIComponent(value);
            if (decodedValue.includes(',')) {
                filterObject[key] = decodedValue.split(',');
            } else {
                // to understand if it should be an array in case of single value. We chack if filter name end with _list
                if (endsWithList(key)) {
                    filterObject[key] = [decodedValue];
                } else {
                    filterObject[key] = decodedValue;
                }
            }
        }
    });

    return filterObject;
};

export const createFilterHash = (filterObject: FilterObject): string => {
    if (!filterObject || Object.keys(filterObject).length === 0) return '';
    if (countNonEmptyProperties(filterObject) < 1) return '';
    const keyValuePairs = Object.entries(filterObject).map(([key, value]) => {
        if (Array.isArray(value)) {
            if (value.length > 0) {
                return `${key}=${value.map((val) => encodeURIComponent(val)).join('%2C')}`;
            }
        } else {
            if (value) {
                return `${key}=${encodeURIComponent(value)}`;
            }
        }
    });
    const clearedArray = keyValuePairs.filter((item) => !!item);
    return clearedArray.join('&');
};

export const updateObjectValues = (obj1: Record<string, any>, obj2: Record<string, any>): any => {
    const result = { ...obj1 };
    for (const key in obj2) {
        if (obj2.hasOwnProperty(key) && obj1.hasOwnProperty(key)) {
            result[key] = obj2[key];
        }
    }
    return result;
};

export const REPORT_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
export const convertDayJsUnixDataToOffsetDateTimeFormat = (data: any) => {
    return dayjs.unix(+data).utc().format(REPORT_DATE_FORMAT);
};

export interface ReportDateRange {
    from: string;
    to: string;
}

const isUnixTimestamp = (value: unknown) =>
    (typeof value === 'number' || (typeof value === 'string' && value.trim() !== '')) &&
    Number.isFinite(Number(value));

/**
 * The date range picker's Reset emits an empty range, and every report API rejects a missing or
 * unparsable date, so a `null` here means "do not request the report".
 * `utc: false` keeps the user's offset in the string, as Transaction summary always sent it.
 */
export const getReportDateRange = (
    range: unknown,
    { utc: inUtc = true }: { utc?: boolean } = {},
): ReportDateRange | null => {
    if (!Array.isArray(range) || range.length < 2 || !range.slice(0, 2).every(isUnixTimestamp)) {
        return null;
    }
    const format = (value: unknown) => {
        const date = dayjs.unix(Number(value));
        return (inUtc ? date.utc() : date).format(REPORT_DATE_FORMAT);
    };
    return { from: format(range[0]), to: format(range[1]) };
};

export const REPORT_LOADING_TEXT = 'Your report is being generated. This may take a few moments.';

export const REPORT_NO_DATE_RANGE_TEXT = {
    main: 'No date range selected',
    secondary: 'Select a date range to see the report',
};
